import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-hot-toast';
import { HiLockClosed, HiEye, HiEyeOff } from 'react-icons/hi';

import { updatedPassword } from '../../redux/actions/user';
import { jwtDecode } from 'jwt-decode';

import { Button } from '../../components/buttons/Button';
import { CopyrightFooter } from '../../components/copyrightFooter/CopyrightFooter';
import { BrandingLogo } from '../../components/brandingLogo/BrandingLogo';

import image from '../../static/image/1.png';

export const FormForgetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useParams();
    const { email } = jwtDecode(token);

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [loading, setLoading] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const onChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const validateForm = () => {
        const { password, confirmPassword } = formData;
        const p = password ?? '';
        const c = confirmPassword ?? '';

        if (!p.trim()) {
            toast.error('Ingresa tu contraseña');
            setLoading(false);
            return false;
        }
        if (!c.trim()) {
            toast.error('Confirma tu contraseña');
            setLoading(false);
            return false;
        }
        if (p !== c) {
            toast.error('Las contraseñas no son las mismas, revisa tus contraseñas');
            setLoading(false);
            return false;
        }
        return true;
    }

    const handleUpdatePassword = (e) => {
        e.preventDefault();
        setLoading(true);
        if (!validateForm()) {
            return;
        }
        const password = formData.password;
        dispatch(updatedPassword({ email, password }))
            .then((result) => {
                if (result.status === 200) {
                    toast.success(result.message);
                    navigate('/');
                } else if (result?.message) {
                    toast.error(result.message);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }

    if (!email)
        return (
            <div className='w-full min-h-screen flex items-center justify-center p-6 bg-white'>
                <h1 className='text-center text-gray-800'>Tu enlace está vencido; genera uno nuevo.</h1>
            </div>
        );

    const renderStep = () => (
        <div className='w-full h-full min-h-0 flex-1 flex flex-col bg-white animate-fadeIn'>
            <div className='w-full max-w-md mx-auto flex flex-col flex-1 min-h-0 px-4 md:px-6'>
                <div className='flex-1 flex flex-col justify-center min-h-0 overflow-y-auto py-6'>
                    <div className='flex justify-center shrink-0 mb-8 animate-slideDown'>
                        <BrandingLogo />
                    </div>
                    <div className='text-center mb-8 animate-slideUp'>
                        <h2 className='text-2xl font-bold text-gray-800 mb-2'>Nueva contraseña</h2>
                        <p className='text-gray-500 text-sm'>
                            Ingresa tu nueva contraseña para acceder a tu cuenta.
                        </p>
                    </div>
                    <form onSubmit={(e) => handleUpdatePassword(e)} className='space-y-6'>
                        <div className='relative animate-slideUp' style={{ animationDelay: '0.1s' }}>
                            <label className='block text-sm font-semibold text-gray-700 mb-2'>
                                Nueva contraseña
                            </label>
                            <div className='relative'>
                                <div className={`absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none transition-colors duration-300 ${
                                    focusedInput === 'password' ? 'text-indigo-600' : 'text-gray-400'
                                }`}>
                                    <HiLockClosed className='w-5 h-5' />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name='password'
                                    value={formData.password}
                                    onChange={onChange}
                                    onFocus={() => setFocusedInput('password')}
                                    onBlur={() => setFocusedInput(null)}
                                    placeholder='••••••••'
                                    disabled={loading}
                                    className={`w-full pl-12 pr-12 py-3.5 border-2 rounded-xl transition-all duration-300 outline-none
                                        ${focusedInput === 'password'
                                            ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-200/50'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                        }
                                        disabled:bg-gray-50 disabled:cursor-not-allowed
                                        focus:ring-4 focus:ring-indigo-100`}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowPassword(!showPassword)}
                                    className='absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-indigo-600 transition-colors duration-300 focus:outline-none'
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <HiEyeOff className='w-5 h-5 transition-transform hover:scale-110' />
                                    ) : (
                                        <HiEye className='w-5 h-5 transition-transform hover:scale-110' />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className='relative animate-slideUp' style={{ animationDelay: '0.2s' }}>
                            <label className='block text-sm font-semibold text-gray-700 mb-2'>
                                Confirma tu nueva contraseña
                            </label>
                            <div className='relative'>
                                <div className={`absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none transition-colors duration-300 ${
                                    focusedInput === 'confirmPassword' ? 'text-indigo-600' : 'text-gray-400'
                                }`}>
                                    <HiLockClosed className='w-5 h-5' />
                                </div>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name='confirmPassword'
                                    value={formData.confirmPassword}
                                    onChange={onChange}
                                    onFocus={() => setFocusedInput('confirmPassword')}
                                    onBlur={() => setFocusedInput(null)}
                                    placeholder='••••••••'
                                    disabled={loading}
                                    className={`w-full pl-12 pr-12 py-3.5 border-2 rounded-xl transition-all duration-300 outline-none
                                        ${focusedInput === 'confirmPassword'
                                            ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-200/50'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                        }
                                        disabled:bg-gray-50 disabled:cursor-not-allowed
                                        focus:ring-4 focus:ring-indigo-100`}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className='absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-indigo-600 transition-colors duration-300 focus:outline-none'
                                    disabled={loading}
                                >
                                    {showConfirmPassword ? (
                                        <HiEyeOff className='w-5 h-5 transition-transform hover:scale-110' />
                                    ) : (
                                        <HiEye className='w-5 h-5 transition-transform hover:scale-110' />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className='animate-slideUp' style={{ animationDelay: '0.3s' }}>
                            <Button
                                type="submit"
                                variant="gradient"
                                fullWidth
                                loading={loading}
                                loadingLabel="Guardando..."
                            >
                                Confirmar cambio de contraseña
                            </Button>
                        </div>

                        <div className='flex justify-center animate-slideUp' style={{ animationDelay: '0.4s' }}>
                            <Link to={'/'} className='text-sm'>
                                <span className='text-gray-700'>¿Ya tienes una cuenta? </span>
                                <span className='font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-300 hover:underline'>Iniciar sesión</span>
                            </Link>
                        </div>
                    </form>
                </div>
                <CopyrightFooter className='flex-shrink-0 pb-6 pt-8 animate-fadeIn' style={{ animationDelay: '0.5s' }} />
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.6s ease-out forwards;
                }
                .animate-slideDown {
                    animation: slideDown 0.6s ease-out forwards;
                }
                .animate-slideUp {
                    animation: slideUp 0.6s ease-out forwards;
                    opacity: 0;
                    animation-fill-mode: forwards;
                }
            `}</style>
        </div>
    );

    return (
        <div className='w-full flex'>
            <div className='w-full h-screen md:w-[40%] flex flex-col min-h-0'>
                {renderStep()}
            </div>
            <div
                className='w-0 md:w-[60%] h-screen bg-cover'
                style={{ backgroundImage: `url(${image})` }}
            ></div>
        </div>
    );
};
